<template>
  <InputContainer
    v-if="
      store.paymentRequestType === PaymentRequestTypeEnum.TIME_X_RATE &&
      timesheetsStore.timesheetType.value !== TimesheetTypeEnum.TIMESHEET
    "
  >
    <template #label>{{ $t('Billable time') }}</template>
    <template #sub-label>
      {{
        $t('Number of :rateUnits worked in this period', {
          rateUnits: $t(props.rateUnits),
        })
      }}
    </template>
    <div class="tw-w-[200px] tw-max-w-full tw-flex tw-flex-row">
      <div>
        <InputDecimal
          v-model:formatted="store.paymentRequestForm.billableTime"
          data-test-prefix="payment-request-billable-time-input"
          name="billable-time"
          :fraction-min="0"
          :fraction-max="99"
          :min="0.0"
          :max="maxBillableTime"
          decimal-point="."
          placeholder="0"
          fraction-placeholder="00"
          :error="
            validationStore.v$.billableTime.$error
              ? expensesStore.enableExpenseReports
                ? $t('Add billable time or attach expenses')
                : $t('Add billable time')
              : undefined
          "
          :disabled="!store.isEditable"
          @blur="validationStore.v$.billableTime.$touch()"
        />
      </div>
      <span class="tw-ml-2 tw-mt-2 tw-font-medium">{{ $t(props.rateUnits) }}</span>
    </div>
  </InputContainer>
  <InputContainer v-if="store.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT">
    <template #label>{{ $t('Billable total') }}</template>
    <template #sub-label>{{ $t('The amount to charge') }}</template>
    <WdsInputCurrency
      v-model="store.paymentRequestForm.billableTotal"
      data-test="payment-request-billable-total-input"
      class="tw-w-[200px] tw-max-w-max"
      :config="inputCurrencyConfig"
      :error="
        validationStore.v$.billableTotal.$error
          ? expensesStore.enableExpenseReports
            ? $t('Add a billable total or attach expenses')
            : $t('Add a billable total')
          : false
      "
      :disabled="!store.isEditable"
      @blur="validationStore.v$.billableTotal.$touch()"
    />
  </InputContainer>
  <WdsAlert v-if="store.paymentRequestType === PaymentRequestTypeEnum.SPECIFY_AMOUNT" icon>
    {{
      $t(
        'Do not populate "Billable total" if you intend to submit an expense-only payment request. Use the dedicated expense report section to create or add your expense(s).',
      )
    }}
  </WdsAlert>
</template>

<script setup lang="ts">
import { WdsAlert, WdsInputCurrency } from '@worksome/wds-components'
import { computed } from 'vue'
import { PaymentRequestTypeEnum, TimesheetTypeEnum } from '@/components/PaymentRequest/enums'
import InputContainer from '@/components/PaymentRequest/InputContainer.vue'
import InputDecimal from '@/components/InputDecimal.vue'
import { useTranslation } from '@/composables/translation'
import { usePaymentRequestStore } from '@/components/PaymentRequest/store/usePaymentRequestStore'
import { useTimesheetStore } from '@/components/PaymentRequest/store/useTimesheetStore'
import { useValidationStore } from '@/components/PaymentRequest/store/useValidationStore'
import { useExpensesStore } from '@/components/PaymentRequest/store/useExpensesStore'

const { $t } = useTranslation()
const store = usePaymentRequestStore()
const timesheetsStore = useTimesheetStore()
const validationStore = useValidationStore()
const expensesStore = useExpensesStore()

const props = withDefaults(defineProps<{ rateUnits: string }>(), {
  rateUnits: '',
})

const maxBillableTimeByUnit: Record<string, number> = {
  hours: 999,
  days: 366,
  weeks: 53,
}

const maxBillableTime = computed(() => maxBillableTimeByUnit[props.rateUnits] ?? 9999)

const inputCurrencyConfig = computed(() => ({
  currencySymbol: store.currency,
  thousandsSeparator: store.currencyConfig.separator,
  decimalSeparator: store.currencyConfig.decimal,
  showFractions: true,
}))
</script>
